import { StrictMode } from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
} from 'react-router-dom';
import { wrapCreateBrowserRouterV6 } from '@sentry/react';
import { QueryClientProvider } from '@tanstack/react-query';

import { routes as ordersRoutes } from '@hcs/apps/order-manager';
import { SentryUser } from '@hcs/authn';
import { CerberusDevTool } from '@hcs/cerberus';
import { marketStateSearchSlice } from '@hcs/cerberus-stats';
import { HcsConsoleDevTool } from '@hcs/console';
import { dataExplorerSlice } from '@hcs/data-explorer';
import { SkeletonPage } from '@hcs/design-system';
import { DesignSystemStyles } from '@hcs/design-system';
import { LayoutContent, LayoutGlobalHeaderChildren } from '@hcs/design-system';
import { HcsDevTools, PageTitleDevTool } from '@hcs/dev-tools';
import {
  ENGAGEMENT_TRACKING_REDUCER_KEY,
  EngagementTrackingDevTool,
  engagementTrackingReducer,
} from '@hcs/engagement-tracking';
import { ExperienceFlagsDevTool } from '@hcs/experience-flags';
import { experienceFlagsSlice } from '@hcs/experience-flags';
import { ReleaseVersionChecker } from '@hcs/hc-products';
import { HelpChatProvider, helpChatSlice } from '@hcs/help-chat';
import { queryClient } from '@hcs/http-clients';
import { keywordSearchSlice } from '@hcs/keyword-search';
import { hcMapSlice } from '@hcs/maps';
import { IntercomUser } from '@hcs/organization';
import { addendumSlice } from '@hcs/pdf/pdf-service';
import {
  PORTFOLIO_REDUCER_KEY,
  portfolioAssetsSlice,
  portfolioNotificationsSlice,
  portfolioReducer,
} from '@hcs/portfolio';
import { REPORT_API_REDUCER_KEY, reportApiReducer } from '@hcs/report-api';
import { RouterErrorBoundary } from '@hcs/routing';
import { SearchByMlsNumberProvider } from '@hcs/search-by-mls-number';
import { toastSlice } from '@hcs/toast';
import { Toast } from '@hcs/toast';
import { createHcReduxStore } from '@hcs/utils';
import { addErrorElementToRoutes } from '@hcs/utils';
import { initSentry, ROOT_QUERY_SELECTOR } from '@hcs/webapps';

import { routes as accessRoutes } from './apps/access/routes';
import { OmDemoPageRedirect } from './auth-redirects/OmDemoPageRedirect';
import { Landing } from './features/Landing/Landing';
import { OmDemoGlobalHeader } from './navigation/OmDemoGlobalHeader';

initSentry();

const sentryCreateBrowserRouter =
  wrapCreateBrowserRouterV6(createBrowserRouter);

const partnerToolsRouter = sentryCreateBrowserRouter(
  addErrorElementToRoutes(
    <LayoutGlobalHeaderChildren header={<OmDemoGlobalHeader />}>
      <LayoutContent>
        <RouterErrorBoundary />
      </LayoutContent>
    </LayoutGlobalHeaderChildren>,
    [
      {
        path: '',
        element: <Outlet />,
        children: [
          {
            path: '/',
            element: <Landing />,
          },
          ...accessRoutes(),
          ...ordersRoutes(OmDemoPageRedirect, OmDemoGlobalHeader)(),
          {
            path: '*',
            element: <Navigate to="/" replace />,
          },
        ],
      },
    ],
  ),
);

const reduxStore = createHcReduxStore(
  [
    toastSlice,
    dataExplorerSlice,
    keywordSearchSlice,
    hcMapSlice,
    experienceFlagsSlice,
    portfolioAssetsSlice,
    portfolioNotificationsSlice,
    marketStateSearchSlice,
    addendumSlice,
    helpChatSlice,
  ],
  {
    [REPORT_API_REDUCER_KEY]: reportApiReducer,
    [PORTFOLIO_REDUCER_KEY]: portfolioReducer,
    [ENGAGEMENT_TRACKING_REDUCER_KEY]: engagementTrackingReducer,
  },
);

const rootElement = document.querySelector(ROOT_QUERY_SELECTOR);
if (!rootElement) {
  throw new Error(
    `Root element not found. Expected to find an element matching selector: ${ROOT_QUERY_SELECTOR}`,
  );
}
const root = ReactDOM.createRoot(rootElement);
root.render(
  <StrictMode>
    <SkeletonPage>
      <Provider store={reduxStore}>
        <QueryClientProvider client={queryClient}>
          <SearchByMlsNumberProvider>
            <HelpChatProvider>
              <RouterProvider router={partnerToolsRouter} />
              <DesignSystemStyles />
              <HcsDevTools />
              <Toast />
              <HcsConsoleDevTool />
              <EngagementTrackingDevTool />
              <ExperienceFlagsDevTool />
              <CerberusDevTool />
              <IntercomUser />
              <SentryUser />
              <PageTitleDevTool />
              <ReleaseVersionChecker />
            </HelpChatProvider>
          </SearchByMlsNumberProvider>
        </QueryClientProvider>
      </Provider>
    </SkeletonPage>
  </StrictMode>,
);
